import { cva, cx, VariantProps } from 'class-variance-authority';
import { forwardRef, HTMLAttributes, ReactNode } from 'react';
import BlockHeader, { BlockHeaderDataProps } from './header/BlockHeader';
import Image, { ImageProps } from 'next/image';
import Spinner from '@/components/spinner/Spinner.tsx';

export const variants = cva(['overflow-hidden', 'relative'], {
	variants: {
		variant: {
			dark: ['bg-dark-900', 'text-white'],
			dark950: ['bg-dark-950', 'text-white'],
			transparent: 'bg-transparent',
		},
		size: {
			sm: ['rounded-md'],
			md: ['rounded-xl'],
			lg: ['rounded-2xl'],
		},
		adptToParentHeight: {
			true: ['flex', 'flex-col', 'h-full'],
		},
		isChildrenWithPadding: {
			true: [],
			false: [],
		},
	},
	compoundVariants: [],
	defaultVariants: {
		variant: 'dark',
		size: 'md',
	},
});

export const variantsChildren = cva(['relative'], {
	variants: {
		size: {
			sm: ['pr-lg', 'pb-lg', 'pl-lg'],
			md: ['pr-3xl', 'pb-3xl', 'pl-3xl'],
			lg: ['pr-5xl', 'pb-5xl', 'pl-5xl'],
		},
		adptToParentHeight: {
			true: ['flex-1', 'overflow-hidden'],
		},
		isChildrenWithPadding: {
			true: [],
			false: ['!p-0'],
		},
	},
	compoundVariants: [
		{ isChildrenWithPadding: true, size: 'sm', className: 'pt-lg' },
		{ isChildrenWithPadding: true, size: 'md', className: 'pt-3xl' },
		{ isChildrenWithPadding: true, size: 'lg', className: 'pt-5xl' },
	],
	defaultVariants: {
		size: 'md',
	},
});

type BlockDataProps = HTMLAttributes<HTMLDivElement> & {
	adptToParentHeight?: boolean;
	isChildrenWithPadding?: boolean;
	isLoading?: boolean;
	backgroundImage?: Partial<ImageProps>;
	header?: BlockHeaderDataProps;
};

type BlockGlobalProps = BlockDataProps &
	VariantProps<typeof variants> & {
		variant?: string;
		size?: string;
	};

const Block = forwardRef<HTMLDivElement, BlockGlobalProps>(
	(
		{
			adptToParentHeight,
			backgroundImage,
			header,
			children,
			isChildrenWithPadding,
			isLoading,
			variant,
			size,
			className,
		},
		ref,
	) => {
		return (
			<div id='block' ref={ref} className={`${cx(variants({ variant, adptToParentHeight, size, className }))}`}>
				{backgroundImage && <Image src={''} fill alt='' className='object-cover' {...backgroundImage} />}
				{!isLoading && (
					<>
						{header && <BlockHeader {...header} size={size} />}
						{children && (
							<div className={`${cx(variantsChildren({ size, adptToParentHeight, isChildrenWithPadding }))}`}>
								{children}
							</div>
						)}
					</>
				)}
				{isLoading && <Spinner variant={'white'} />}
			</div>
		);
	},
);

Block.displayName = 'Block';
export default Block;
